import { Box, Typography } from "@mui/material";
import { useMemo, memo } from "react";

import AdImageViewer from "~src/components/AdImageViewer";
import LazyVideoPlayer from "~src/components/LazyVideoPlayer";
import { TrendingAd } from "~src/types/ad";
import { truncateText } from "~src/utils/string";

// Utility function to sanitize HTML content
const sanitizeHTML = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const BodyComponent = ({ ad }: { ad: TrendingAd }) => {
  // Memoize sanitized text to avoid DOM parsing on every render
  const sanitizedText = useMemo(() => sanitizeHTML(ad.ad_content), [ad.ad_content]);

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
      }}
      data-testid="fb-ads-body"
    >
      <AdBody text={sanitizedText} />
      {ad.ad_video && ad.ad_video !== "N/A" ? (
        <LazyVideoPlayer sourceUrl={ad.ad_video} />
      ) : (
        <Box
          sx={{
            mt: 2,
            margin: "0px !important",
            width: "100% !important",
            height: "312px !important",
            background: "#d3d3d3",
            borderRadius: "5px",
            position: "relative",
            display: "inline-block",
            overflow: "hidden",
          }}
        >
          <AdImageViewer sourceUrl={ad.ad_image} />
        </Box>
      )}
    </Box>
  );
};

// Memoize to prevent re-renders when parent updates
export const Body = memo(BodyComponent);

const AdBody = ({ text }: { text?: string }) => {
  const adText = useMemo(() => {
    if (!text || text === "N/A") {
      return "No text for this ad";
    }

    return text;
  }, [text]);

  return (
    <Typography
      sx={{
        fontSize: "10px",
        fontWeight: 500,
        lineHeight: "14px",
        color: "grey.500",
        height: "56px",
      }}
    >
      {truncateText(adText, 150)}
    </Typography>
  );
};
